/**
 * TUVI - Novedades del proyecto
 * Compromisos que quedan pactados con el cliente: responsable (el residente),
 * qué se comprometió, para cuándo, quién lo negoció y en qué va. Todo movimiento
 * queda registrado.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNotification } from '../../../context/NotificationContext';
import { useAuth } from '../../../context/AuthContext';
import client from '../../../services/graphqlClient';
import { UPLOAD_URL } from '../../../config';
import './BitacoraProyecto.css';

const hoyISO = () => new Date().toISOString().split('T')[0];
const fmtFecha = (f) => f ? new Date(f + 'T12:00:00').toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Sin fecha';
const fmtMomento = (f) => f ? new Date(f).toLocaleString('es-CO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
// Compensatorio: texto libre. Si lo escrito es solo un valor, se formatea con
// puntos de miles mientras se escribe ("$ 1.500.000"); si lleva texto, queda tal cual.
const formatearCompensatorio = (texto) => {
  const t = String(texto || '');
  if (!/^\$?\s?[\d.]*$/.test(t)) return t;
  const d = t.replace(/[^0-9]/g, '');
  return d ? '$ ' + Number(d).toLocaleString('es-CO') : t;
};
const textoCompensatorio = (c) =>
  c.compensatorio || (c.valor_compensatorio > 0 ? '$ ' + Math.round(c.valor_compensatorio).toLocaleString('es-CO') : '');
// Al editar: mover o quitar una fecha que ya existía exige motivo; poner la primera, no.
const requiereMotivoFecha = (m) => m?.modo === 'editar' && !!m.original?.fecha_entrega
  && (m.datos.fecha_entrega || '') !== m.original.fecha_entrega;

const ESTADOS = [
  { id: 'pendiente', label: 'Pendiente' },
  { id: 'en_proceso', label: 'En proceso' },
  { id: 'cumplido', label: 'Cumplido' },
  { id: 'cancelado', label: 'Cancelado' },
];

const textoDias = (d, estado) => {
  if (estado === 'cumplido' || estado === 'cancelado') return '';
  if (d == null) return '';
  if (d < 0) return `vencido hace ${Math.abs(d)} día${Math.abs(d) === 1 ? '' : 's'}`;
  if (d === 0) return 'vence hoy';
  if (d === 1) return 'vence mañana';
  return `vence en ${d} días`;
};

const BitacoraProyecto = ({ proyectoId, onCambio }) => {
  const { success, error } = useNotification();
  const { user } = useAuth();
  const [datos, setDatos] = useState({ compromisos: [], resumen: null, puede_crear: false });
  const [cargando, setCargando] = useState(true);
  const [modal, setModal] = useState(null);      // { modo:'crear'|'editar', datos }
  const [expandido, setExpandido] = useState(null);
  const [comentario, setComentario] = useState('');
  const [subiendo, setSubiendo] = useState(false);
  const [guardando, setGuardando] = useState(false); // evita el doble registro por doble clic

  const nombreUsuario = user?.nombre_completo || user?.username || '';
  const camposVacios = () => ({ descripcion: '', fecha_entrega: '', negociador: nombreUsuario, compensatorio: '' });

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const d = await client.query(`query($p: Int!) { bitacoraProyecto(proyecto_id: $p) {
        puede_crear residente_nombre residente_email
        resumen { vencidos proximos pendientes cumplidos total }
        compromisos {
          id descripcion fecha_entrega estado semaforo dias_para_vencer
          responsable_id responsable_nombre responsable_email creado_por_nombre cerrado_por_nombre cerrado_at
          motivo_cierre negociador valor_compensatorio compensatorio created_at
          historial { id tipo nota usuario_nombre estado_anterior estado_nuevo fecha_anterior fecha_nueva created_at }
          adjuntos { id archivo_key nombre_original subido_por_nombre created_at }
        } } }`, { p: parseInt(proyectoId) });
      setDatos(d.bitacoraProyecto);
      onCambio && onCambio(d.bitacoraProyecto.resumen);
    } catch { error('No se pudieron cargar las novedades'); }
    finally { setCargando(false); }
  }, [proyectoId, error, onCambio]);

  useEffect(() => { cargar(); }, [cargar]);

  const mutar = async (query, vars, exito) => {
    try {
      const r = await client.mutate(query, vars);
      const res = Object.values(r)[0];
      if (res.success) { success(exito || res.message); await cargar(); return true; }
      error(res.message || 'No se pudo completar la acción');
      return false;
    } catch { error('Error de conexión'); return false; }
  };

  const guardar = async () => {
    if (guardando) return;
    const m = modal;
    const v = m.datos;
    if (!v.descripcion.trim()) { error('Escribe la descripción del compromiso'); return; }
    const comp = (v.compensatorio || '').trim() || null;
    const neg = (v.negociador || '').trim() || null;

    setGuardando(true);
    try {
      if (m.modo === 'crear') {
        const ok = await mutar(
          `mutation($p: Int!, $d: String!, $f: String, $n: String, $c: String) {
            crearCompromisoBitacora(proyecto_id: $p, descripcion: $d, fecha_entrega: $f, negociador: $n, compensatorio: $c) { success message } }`,
          { p: parseInt(proyectoId), d: v.descripcion.trim(), f: v.fecha_entrega || null, n: neg, c: comp });
        if (ok) setModal(null);
      } else {
        // Mover o quitar una fecha que ya existía exige motivo; poner la primera, no.
        if (requiereMotivoFecha(m) && !(v.motivo || '').trim()) {
          error('Cambiaste la fecha de entrega: escribe el motivo');
          return;
        }
        const ok = await mutar(
          `mutation($id: Int!, $d: String, $f: String, $n: String, $c: String, $m: String) {
            editarCompromisoBitacora(id: $id, descripcion: $d, fecha_entrega: $f, negociador: $n, compensatorio: $c, motivo: $m) { success message } }`,
          { id: m.id, d: v.descripcion.trim(), f: v.fecha_entrega || '', n: neg, c: comp, m: v.motivo || null });
        if (ok) setModal(null);
      }
    } finally {
      setGuardando(false);
    }
  };

  const cambiarEstado = async (c, estado) => {
    let nota = null;
    if (estado === 'cumplido' || estado === 'cancelado') {
      nota = window.prompt(estado === 'cumplido'
        ? '¿Cómo se resolvió este compromiso?'
        : '¿Por qué se cancela este compromiso?');
      if (nota === null) return;
      if (!nota.trim()) { error('Debes escribir la nota'); return; }
    }
    await mutar(
      `mutation($id: Int!, $e: String!, $n: String) { cambiarEstadoCompromiso(id: $id, estado: $e, nota: $n) { success message } }`,
      { id: c.id, e: estado, n: nota });
  };

  const enviarComentario = async (c) => {
    if (!comentario.trim()) return;
    const ok = await mutar(
      `mutation($id: Int!, $n: String!) { comentarCompromiso(id: $id, nota: $n) { success message } }`,
      { id: c.id, n: comentario.trim() }, 'Comentario agregado');
    if (ok) setComentario('');
  };

  const subirAdjunto = async (c, file) => {
    if (!file) return;
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append('compromiso_id', c.id);
      fd.append('archivo', file);
      const res = await fetch(`${UPLOAD_URL}/bitacora-adjunto`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('tuvi_token')}` },
        body: fd,
      });
      const j = await res.json();
      if (j.success) { success('Adjunto agregado'); await cargar(); }
      else error(j.message || 'No se pudo subir el archivo');
    } catch { error('Error al subir el archivo'); }
    finally { setSubiendo(false); }
  };

  const eliminar = async (c) => {
    if (!window.confirm(`¿Eliminar este compromiso?\n\n"${c.descripcion}"\n\nSe borra también su historial y sus adjuntos.`)) return;
    await mutar(`mutation($id: Int!) { eliminarCompromisoBitacora(id: $id) { success message } }`, { id: c.id });
  };

  const abrirAdjunto = (a) => {
    const url = `${UPLOAD_URL}/bitacora-adjunto/${encodeURIComponent(a.archivo_key)}?token=${localStorage.getItem('tuvi_token')}`;
    window.open(url, '_blank');
  };

  // El responsable es el residente del proyecto: se muestra, no se elige.
  const { compromisos, puede_crear, residente_nombre: residente, residente_email } = datos;
  const setCampo = (campo, valor) => setModal(m => ({ ...m, datos: { ...m.datos, [campo]: valor } }));

  if (cargando) return <div className="bit-cargando">Cargando novedades...</div>;

  return (
    <div className="bit-wrap">
      <div className="bit-header">
        <div>
          <h3>Novedades del proyecto</h3>
          <p>Lo que queda pactado con el cliente, con responsable, negociador y fecha de entrega.</p>
        </div>
        {puede_crear ? (
          <button className="bit-btn bit-btn-crear" onClick={() => setModal({ modo: 'crear', datos: camposVacios() })}>
            + Nuevo compromiso
          </button>
        ) : (
          <span className="bit-sin-permiso" title="Se necesita el permiso «Novedades - Crear, editar y eliminar compromisos»">
            Solo puedes consultar, cambiar el estado, comentar y adjuntar
          </span>
        )}
      </div>

      {compromisos.length === 0 ? (
        <div className="bit-vacio">Todavía no hay compromisos registrados en este proyecto.</div>
      ) : (
        <div className="bit-lista">
          {compromisos.map(c => {
            const abierto = expandido === c.id;
            return (
              <div key={c.id} className={`bit-item sem-${c.semaforo}`}>
                <div className="bit-item-top" onClick={() => { setExpandido(abierto ? null : c.id); setComentario(''); }}>
                  <div className="bit-item-info">
                    <div className="bit-item-desc">{c.descripcion}</div>
                    <div className="bit-item-meta">
                      <span className="bit-resp">{c.responsable_nombre || 'Sin residente asignado'}</span>
                      <span className="bit-sep">·</span>
                      <span>{fmtFecha(c.fecha_entrega)}</span>
                      {textoDias(c.dias_para_vencer, c.estado) && (
                        <>
                          <span className="bit-sep">·</span>
                          <span className={`bit-dias ${c.semaforo}`}>{textoDias(c.dias_para_vencer, c.estado)}</span>
                        </>
                      )}
                      {textoCompensatorio(c) && (
                        <span className="bit-valor-chip">{textoCompensatorio(c)}</span>
                      )}
                    </div>
                  </div>
                  <div className="bit-item-acciones">
                    <span className={`bit-estado ${c.estado}`}>
                      {ESTADOS.find(e => e.id === c.estado)?.label || c.estado}
                    </span>
                    <span className="bit-chevron">{abierto ? '▲' : '▼'}</span>
                  </div>
                </div>

                {abierto && (
                  <div className="bit-item-detalle">
                    <div className="bit-detalle-cols">
                      <div className="bit-detalle-datos">
                        {c.negociador && <div><b>Negociador:</b> {c.negociador}</div>}
                        {textoCompensatorio(c) && (
                          <div><b>Compensatorio:</b> <span className="bit-valor">{textoCompensatorio(c)}</span></div>
                        )}
                        <div><b>Registrado por:</b> {c.creado_por_nombre || '—'}</div>
                        {c.cerrado_por_nombre && (
                          <div><b>Cerrado por:</b> {c.cerrado_por_nombre} · {fmtMomento(c.cerrado_at)}</div>
                        )}
                        {c.motivo_cierre && <div className="bit-cierre">"{c.motivo_cierre}"</div>}
                      </div>

                      <div className="bit-detalle-botones">
                        {c.estado !== 'cumplido' && c.estado !== 'cancelado' && (
                          <>
                            {c.estado === 'pendiente' && (
                              <button className="bit-btn bit-btn-sm" onClick={() => cambiarEstado(c, 'en_proceso')}>Marcar en proceso</button>
                            )}
                            <button className="bit-btn bit-btn-sm bit-ok" onClick={() => cambiarEstado(c, 'cumplido')}>✓ Cumplido</button>
                            {puede_crear && (
                              <button className="bit-btn bit-btn-sm" onClick={() => setModal({
                                modo: 'editar', id: c.id,
                                original: { fecha_entrega: c.fecha_entrega },
                                datos: {
                                  descripcion: c.descripcion, fecha_entrega: c.fecha_entrega || '',
                                  negociador: c.negociador || '',
                                  compensatorio: textoCompensatorio(c),
                                  motivo: '',
                                },
                              })}>Editar</button>
                            )}
                          </>
                        )}
                        {(c.estado === 'cumplido' || c.estado === 'cancelado') && (
                          <button className="bit-btn bit-btn-sm" onClick={() => cambiarEstado(c, 'pendiente')}>Reabrir</button>
                        )}
                        <label className="bit-btn bit-btn-sm bit-adj">
                          {subiendo ? 'Subiendo...' : '📎 Adjuntar'}
                          <input type="file" hidden disabled={subiendo}
                            onChange={e => { subirAdjunto(c, e.target.files?.[0]); e.target.value = ''; }} />
                        </label>
                        {puede_crear && (
                          <button className="bit-btn bit-btn-sm bit-del" onClick={() => eliminar(c)}>Eliminar</button>
                        )}
                      </div>
                    </div>

                    {c.adjuntos.length > 0 && (
                      <div className="bit-adjuntos">
                        {c.adjuntos.map(a => (
                          <button key={a.id} className="bit-chip" onClick={() => abrirAdjunto(a)} title={`Subido por ${a.subido_por_nombre || '—'}`}>
                            📎 {a.nombre_original || 'archivo'}
                          </button>
                        ))}
                      </div>
                    )}

                    <div className="bit-historial">
                      <div className="bit-historial-tit">Seguimiento</div>
                      {c.historial.map(h => (
                        <div key={h.id} className="bit-mov">
                          <span className="bit-mov-fecha">{fmtMomento(h.created_at)}</span>
                          <span className="bit-mov-user">{h.usuario_nombre || '—'}</span>
                          <span className="bit-mov-txt">
                            {h.tipo === 'reprogramacion' && (
                              <b>Reprogramó {fmtFecha(h.fecha_anterior)} → {fmtFecha(h.fecha_nueva)}: </b>
                            )}
                            {h.tipo === 'estado' && h.estado_anterior && (
                              <b>{h.estado_anterior.replace('_', ' ')} → {h.estado_nuevo.replace('_', ' ')}{h.nota ? ': ' : ''}</b>
                            )}
                            {h.nota}
                          </span>
                        </div>
                      ))}
                      <div className="bit-comentar">
                        <input
                          placeholder="Escribe un comentario de seguimiento..."
                          value={comentario}
                          onChange={e => setComentario(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); enviarComentario(c); } }}
                        />
                        <button className="bit-btn bit-btn-sm" onClick={() => enviarComentario(c)} disabled={!comentario.trim()}>
                          Comentar
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {modal && (
        <div className="bit-overlay" onClick={() => setModal(null)}>
          <div className="bit-modal" onClick={e => e.stopPropagation()}>
            <div className="bit-modal-head">
              <h3>{modal.modo === 'crear' ? 'Nuevo compromiso' : 'Editar compromiso'}</h3>
              <button className="bit-cerrar" onClick={() => setModal(null)}>×</button>
            </div>
            <div className="bit-modal-body">
              <div className="bit-residente">
                <span>Responsable</span>
                <strong>{residente || 'Sin residente asignado'}</strong>
                <small>
                  {!residente
                    ? 'Este proyecto no tiene residente. El compromiso se registra igual y queda sin responsable; el aviso llega solo a los correos configurados en Alertas.'
                    : residente_email
                      ? `Es el residente asignado al proyecto. Se le avisa a ${residente_email}.`
                      : 'Es el residente asignado al proyecto. No tiene correo registrado, así que no recibirá el aviso.'}
                </small>
              </div>

              <label className="bit-campo">
                <span>Compromiso <b>*</b></span>
                <textarea rows={3} maxLength={1000} placeholder="Qué quedó pactado..."
                  value={modal.datos.descripcion}
                  onChange={e => setCampo('descripcion', e.target.value)} />
              </label>

              <div className="bit-fila">
                <label className="bit-campo">
                  <span>Nueva fecha de entrega</span>
                  <input type="date" value={modal.datos.fecha_entrega || ''}
                    onChange={e => setCampo('fecha_entrega', e.target.value)} />
                  {modal.datos.fecha_entrega
                    ? <button type="button" className="bit-link-quitar" onClick={() => setCampo('fecha_entrega', '')}>Quitar fecha</button>
                    : <small>Si la dejas vacía queda «Sin fecha»</small>}
                </label>
                <label className="bit-campo">
                  <span>Negociador</span>
                  <input type="text" maxLength={150} placeholder="Quién negoció el compromiso"
                    value={modal.datos.negociador}
                    onChange={e => setCampo('negociador', e.target.value)} />
                </label>
              </div>

              <label className="bit-campo">
                <span>Compensatorio</span>
                <input type="text" maxLength={500} placeholder="Valor o descripción (ej: $ 500.000 o cambio de grifería)"
                  value={modal.datos.compensatorio || ''}
                  onChange={e => setCampo('compensatorio', formatearCompensatorio(e.target.value))} />
              </label>

              {requiereMotivoFecha(modal) && (
                <label className="bit-campo bit-motivo">
                  <span>Motivo del cambio de fecha <b>*</b></span>
                  <textarea rows={2} placeholder="Por qué se mueve la fecha de entrega..."
                    value={modal.datos.motivo || ''}
                    onChange={e => setCampo('motivo', e.target.value)} />
                  <small>Queda registrado en el seguimiento del compromiso.</small>
                </label>
              )}
            </div>
            <div className="bit-modal-pie">
              <button className="bit-btn" onClick={() => setModal(null)}>Cancelar</button>
              <button className="bit-btn bit-btn-crear" onClick={guardar} disabled={guardando}>
                {guardando ? 'Guardando...' : (modal.modo === 'crear' ? 'Registrar compromiso' : 'Guardar cambios')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BitacoraProyecto;
